import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Droplets,
  Moon,
  Brain,
  Zap,
  Footprints,
  Utensils,
  HeartPulse,
  Flower2,
  Star,
  Flame,
  Target,
  Trophy,
  Calendar,
  CheckCircle2,
} from "lucide-react";
import { useHabitAnalytics } from "@/hooks/use-habit-analytics";
import { cn } from "@/lib/utils";

interface HabitComplianceCardProps {
  clientId: string;
  days?: number;
  className?: string;
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  hydration: <Droplets className="h-4 w-4" />,
  sleep: <Moon className="h-4 w-4" />,
  stress: <Brain className="h-4 w-4" />,
  energy: <Zap className="h-4 w-4" />,
  movement: <Footprints className="h-4 w-4" />,
  nutrition: <Utensils className="h-4 w-4" />,
  recovery: <HeartPulse className="h-4 w-4" />,
  mindfulness: <Flower2 className="h-4 w-4" />,
  custom: <Star className="h-4 w-4" />,
};

const CATEGORY_COLORS: Record<string, { bg: string; text: string; progress: string }> = {
  hydration: { bg: "bg-chart-2/10", text: "text-chart-2", progress: "bg-chart-2" },
  sleep: { bg: "bg-chart-4/10", text: "text-chart-4", progress: "bg-chart-4" },
  stress: { bg: "bg-chart-7/10", text: "text-chart-7", progress: "bg-chart-7" },
  energy: { bg: "bg-chart-3/10", text: "text-chart-3", progress: "bg-chart-3" },
  movement: { bg: "bg-chart-1/10", text: "text-chart-1", progress: "bg-chart-1" },
  nutrition: { bg: "bg-chart-6/10", text: "text-chart-6", progress: "bg-chart-6" },
  recovery: { bg: "bg-chart-5/10", text: "text-chart-5", progress: "bg-chart-5" },
  mindfulness: { bg: "bg-chart-4/10", text: "text-chart-4", progress: "bg-chart-4" },
  custom: { bg: "bg-muted/50", text: "text-muted-foreground", progress: "bg-muted-foreground" },
};

function getComplianceColor(rate: number): string {
  if (rate >= 80) return "text-success";
  if (rate >= 60) return "text-warning";
  if (rate >= 40) return "text-warning";
  return "text-destructive";
}

function getComplianceBadge(rate: number): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } {
  if (rate >= 80) return { label: "Excellent", variant: "default" };
  if (rate >= 60) return { label: "Good", variant: "secondary" };
  if (rate >= 40) return { label: "Fair", variant: "outline" };
  return { label: "Needs Work", variant: "destructive" };
}

export function HabitComplianceCard({
  clientId,
  days = 30,
  className,
}: HabitComplianceCardProps) {
  const { analytics, loading } = useHabitAnalytics(clientId, days);

  if (loading) {
    return (
      <Card className={cn("", className)}>
        <CardHeader className="pb-2">
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!analytics || analytics.totalDays === 0) {
    return (
      <Card className={cn("", className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="h-5 w-5 text-primary" />
            Habit Compliance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Target className="h-12 w-12 mb-2 opacity-20" />
            <p className="text-sm">No habit data available</p>
            <p className="text-xs">Assign habits to start tracking compliance</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const badge = getComplianceBadge(analytics.overallCompliance);

  return (
    <Card className={cn("", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Habit Compliance
          </span>
          <Badge variant={badge.variant}>{badge.label}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Stats */}
        <div className="grid grid-cols-4 gap-3">
          <div className="text-center p-3 bg-primary/5 rounded-lg">
            <div
              className={cn(
                "text-2xl font-bold",
                getComplianceColor(analytics.overallCompliance)
              )}
            >
              {analytics.overallCompliance}%
            </div>
            <div className="text-xs text-muted-foreground">Overall</div>
          </div>
          <div className="text-center p-3 bg-warning/10 rounded-lg">
            <div className="text-2xl font-bold text-warning flex items-center justify-center gap-1">
              <Flame className="h-5 w-5" />
              {analytics.currentStreak}
            </div>
            <div className="text-xs text-muted-foreground">Streak</div>
          </div>
          <div className="text-center p-3 bg-warning/10 rounded-lg">
            <div className="text-2xl font-bold text-warning flex items-center justify-center gap-1">
              <Trophy className="h-5 w-5" />
              {analytics.longestStreak}
            </div>
            <div className="text-xs text-muted-foreground">Best Streak</div>
          </div>
          <div className="text-center p-3 bg-success/10 rounded-lg">
            <div className="text-2xl font-bold text-success flex items-center justify-center gap-1">
              <CheckCircle2 className="h-5 w-5" />
              {analytics.totalCompletions}
            </div>
            <div className="text-xs text-muted-foreground">Completed</div>
          </div>
        </div>

        {/* Category Breakdown */}
        {analytics.byCategory.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">By Category</h4>
            <div className="space-y-2">
              {analytics.byCategory.map((cat) => {
                const colors = CATEGORY_COLORS[cat.category] || CATEGORY_COLORS.custom;
                return (
                  <div
                    key={cat.category}
                    className={cn("p-3 rounded-lg", colors.bg)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={colors.text}>
                          {CATEGORY_ICONS[cat.category] || CATEGORY_ICONS.custom}
                        </span>
                        <span className="font-medium capitalize text-sm">
                          {cat.category}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({cat.habits.length} habit{cat.habits.length !== 1 ? "s" : ""})
                        </span>
                      </div>
                      <span className={cn("font-bold", colors.text)}>
                        {cat.complianceRate}%
                      </span>
                    </div>
                    <Progress
                      value={cat.complianceRate}
                      className="h-2"
                    />
                    <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
                      <span>
                        {cat.completedLogs}/{cat.totalLogs} logged
                      </span>
                      <span className="truncate max-w-[200px]">
                        {cat.habits.slice(0, 2).join(", ")}
                        {cat.habits.length > 2 && ` +${cat.habits.length - 2}`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Period Info */}
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2 border-t">
          <Calendar className="h-3 w-3" />
          <span>Last {days} days • {analytics.totalDays} days with data</span>
        </div>
      </CardContent>
    </Card>
  );
}
